import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import EditCapitalizationModal from './edit-capitalization-modal/Modal';
import {
  Dimmer,
  Table,
  Segment,
  Loader,
  Popup,
  Menu,
  Label
} from 'semantic-ui-react';
import { iRootDispatch, iRootState } from '../../../store';
import { connect, useSelector } from 'react-redux';
import { AvailableBrokersAndEstatesState } from '../../../store/types/temp-types';
import { maskCpfOrCnpj } from '../../../services/masks';
import DetailModal from './DetailModal';
import styled from 'styled-components';
import { translateStatusCapitalization } from '../util';
import ModalAttachFiles from '../../../components/ModalAttachFiles';
import { UserRolesEnum } from '../../../enums/user-roles.enum';
import moment from 'moment-timezone';
import { getCapitalizations } from '../../../services/capitalization';
import { ListCapitalizationInterface } from '../ListCapitalizationInitial.interface';
import { CapitalizationStatusEnum } from '../enums/capitalization-status.enum';
import { CustoDropDownItemProps } from '../../../components/types';
import { FileIdentifiersCapitalizationType } from '../types/fileidentifiers.capitalization.type';
import FollowUpModal from '../../../components/GeneralFollowUpModal';
import { PurposesEnum } from '../../../enums/purposes.enum';
import { sendMessage } from '../../../services/generalfollowup';
import EstateUnitFilter from '../../../components/EstateUnitFilter';
import { appendEstateUnitIdToFilter } from '../../../utils/estateUnitFilter.util';
import { getListEmptyMessage } from '../../../utils/estateUnitEmptyState.util';
import AuthorizationContainer from '../../../components/AuthorizationContainer';
import { ActionEnum } from '../../../enums/authz-action.enum';
import { FeatureEnum } from '../../../enums/authz-feature.enum';
import { useDropDownUsers } from '../../../services/broker';
import { UsersToCustomDropDownOptions } from '../../../util';
import { useQuery } from '@tanstack/react-query';
import { FollowUpType } from '../../../types/FollowUp';
import { get } from '../../../services/storage';
import { isBackOfficeUser } from '../../../utils/isBackOfficeUser';
import TablePaginationMenu from '../../../components/TablePaginationMenu';
import Button from '../../../components/Button';
import { useLocation, useHistory } from 'react-router-dom';
import { isEstateRole } from '../../../utils/isEstateRole';
import PopupModal from '../../../components/PopupModal';
import { BannerLocationActivationEnum } from '../../../types/Banner';
import CapitalizationLenderInsuranceModal from './CapitalizationLenderInsuranceModal';
import { calculateAge } from '../../../utils/calculateAge';
import { typeOptions } from '../../fire-insurance/policy-list/types';
import FilterBar, { FilterField, FilterValues } from '../../../components/Filter';

type SearchField = 'document' | 'name' | 'address';

type ApiFilters = {
  estateId?: string | null;
  insurerId?: string | null;
  status?: string | null;
  start?: string | null;
  end?: string | null;
  name?: string | null;
  document?: string | null;
  address?: string | null;
  accountManager?: string | null;
  estateUnitId?: string;
  onlyUnread?: boolean;
  userId?: string | null;
  isRv?: boolean;
  id?: string | null;
  page?: number;
};

const SEARCH_FIELDS: SearchField[] = ['document', 'name', 'address'];

const ClickableTableCell = styled(Table.Cell)`
  cursor: pointer;
  position: relative;
`;

const ClickableTableRow = styled(Table.Row)`
  :hover {
    background-color: #f0f0f0;
  }
`;

export const FieldLabel = styled.label<{ paddingTop?: boolean }>`
  font-weight: bold;
  padding-top: ${props => (props.paddingTop ? '14px' : '0px')};
  text-transform: uppercase;
  margin-left: 5px;
`;

export const FieldValue = styled.p`
  padding-left: 5px;
`;

const mapState = (state: iRootState) => ({
  availableBrokersAndEstates: state.availableBrokersAndEstates,
  user: state.user
});

const mapDispatch = (dispatch: iRootDispatch) => ({
  updateAvailableBrokersAndEstates: (availableBrokers: AvailableBrokersAndEstatesState) =>
    dispatch.availableBrokersAndEstates.updateAvailableBrokersAndEstates(availableBrokers)
});

interface LocationState {
  capitalizationId?: string;
}

const toSelectOptions = (
  items: Array<{ value: any; text: string }>
): Array<{ value: string; label: string }> =>
  items.map(item => ({
    value: String(item.value),
    label: String(item.text)
  }));

const formatDateValue = (value: any): string | undefined => {
  if (!value) return undefined;
  if (value instanceof Date) {
    return moment(value).format('YYYY-MM-DD');
  }
  const asString = String(value);
  if (/^\d{4}-\d{2}-\d{2}/.test(asString)) {
    return asString.substring(0, 10);
  }
  const parsed = moment(asString, ['YYYY-MM-DD', 'DD/MM/YYYY'], true);
  return parsed.isValid() ? parsed.format('YYYY-MM-DD') : asString;
};

const CapitalizationTable = (props: any) => {
  const location = useLocation();
  const history = useHistory();
  const { availableBrokersAndEstates, user, insurers } = props;
  const estates: CustoDropDownItemProps[] = availableBrokersAndEstates.estates;

  const [currentPage, setCurrentPage] = useState(1);
  const [selectedCapitalization, setSelectedCapitalization] = useState<any>(false);
  const [openDetail, setOpenDetail] = useState(false);
  const [idFollowUp, setIdFollowUp] = useState('');
  const [bondInsuranceIdOpen, setBondInsuranceIdOpen] = useState('');
  const [openFollowUp, setOpenFollowUp] = useState(false);
  const [total, setTotal] = useState(0);
  const [openLenderInsuranceModal, setOpenLenderInsuranceModal] = useState(false);
  const [selectedCapitalizationForLender, setSelectedCapitalizationForLender] =
    useState<ListCapitalizationInterface | null>(null);

  const isBackOffice = isBackOfficeUser();
  const isEstateUser = isEstateRole(user?.role);
  const isEstateQuotationUser = user?.role === UserRolesEnum.estateQuotation;
  const activeEstateId =
    availableBrokersAndEstates?.activeEstateId || user?.activeEstateId || user?.estate?.id;

  const globalLenderInsuranceEnabled = useSelector(
    (state: iRootState) => state.availableBrokersAndEstates.lenderInsuranceEnabled ?? false
  );
  const globalLenderInsuranceRate = useSelector(
    (state: iRootState) => state.availableBrokersAndEstates.lenderInsuranceRate
  );

  const singleEstateId = useMemo(() => {
    if (estates.length !== 1) return '';
    const value = estates[0]?.value;
    return String(typeof value === 'object' && value?.id ? value.id : value || '');
  }, [estates]);

  const defaultEstateId = useMemo(() => {
    if (isEstateUser) {
      return String(activeEstateId || singleEstateId || '');
    }
    return singleEstateId;
  }, [activeEstateId, isEstateUser, singleEstateId]);

  const locationState = location.state as LocationState | undefined;
  const initialCapitalizationId =
    locationState?.capitalizationId ||
    new URLSearchParams(location.search).get('id') ||
    null;
  const hasDeepLink = Boolean(initialCapitalizationId);

  const initialFilterValues = useMemo(
    (): FilterValues => ({
      type: 'name',
      argument: '',
      insurerId: '',
      estateId: defaultEstateId,
      status: '',
      start: '',
      end: '',
      estateUnitId: '',
      accountManager: ''
    }),
    [defaultEstateId]
  );

  const [appliedFilters, setAppliedFilters] = useState<ApiFilters>(() => ({
    estateId: defaultEstateId || null
  }));
  const [isEnabledToFetch, setIsEnabledToFetch] = useState(
    hasDeepLink || !isBackOffice
  );
  const [capitalizationId, setCapitalizationId] = useState<string | null>(
    initialCapitalizationId
  );

  const appliedFiltersRef = useRef(appliedFilters);
  const capitalizationIdRef = useRef(capitalizationId);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  useEffect(() => {
    capitalizationIdRef.current = capitalizationId;
  }, [capitalizationId]);

  useEffect(() => {
    const capIdFromLocation = (location.state as LocationState)?.capitalizationId;
    const capIdFromQuery = new URLSearchParams(location.search).get('id');
    const nextId = capIdFromLocation || capIdFromQuery || null;
    if (nextId) {
      setCapitalizationId(nextId);
      setIsEnabledToFetch(true);
    }
  }, [location.state, location.search]);

  const handleOpen = () => {
    setOpenFollowUp(true);
  };

  const handleClose = () => {
    setOpenFollowUp(false);
  };

  const { data: usersData, isLoading: isAccountManagerLoading } = useDropDownUsers(
    props?.user?.broker?.id
  );

  const accountManagerOptions = useMemo(() => {
    if (!usersData) return [];

    return UsersToCustomDropDownOptions(
      false,
      usersData.map((item: any) => ({
        id: item.id,
        name: item.name
      }))
    ).map((item: any) => ({
      value: String(item.value),
      text: String(item.text)
    }));
  }, [usersData]);

  const statusSelectOptions = useMemo(
    () =>
      Object.keys(CapitalizationStatusEnum).map(key => ({
        value: key,
        text: translateStatusCapitalization(
          (CapitalizationStatusEnum as any)[key] as string
        )
      })),
    []
  );

  const insurerOptions = useMemo(
    (): Array<{ value: string; text: string }> =>
      (insurers || []).map((item: any) => ({
        value: String(item.value),
        text: String(item.text)
      })),
    [insurers]
  );

  const estateOptions = useMemo(
    () =>
      estates.map(item => {
        const valueId =
          typeof item.value === 'object' && item.value?.id
            ? item.value.id
            : item.value;
        return {
          value: String(valueId || ''),
          text: String(item.text || '')
        };
      }),
    [estates]
  );

  const normalizeToApiFilter = useCallback(
    (values: FilterValues): ApiFilters => {
      const searchType = values.type as SearchField | undefined;
      let argument = String(values.argument || '').trim();

      if (searchType === 'document') {
        argument = argument.replace(/\D/g, '');
      }

      const filter: ApiFilters = {
        onlyUnread: false,
        isRv: true
      };

      SEARCH_FIELDS.forEach(field => {
        filter[field] = null;
      });

      if (searchType && argument && SEARCH_FIELDS.includes(searchType)) {
        filter[searchType] = argument;
      }

      if (values.insurerId) filter.insurerId = String(values.insurerId);

      const selectedEstateId = values.estateId ? String(values.estateId) : '';
      if (isEstateUser) {
        filter.estateId = selectedEstateId || defaultEstateId || null;
      } else if (selectedEstateId) {
        filter.estateId = selectedEstateId;
      } else if (singleEstateId) {
        filter.estateId = singleEstateId;
      } else {
        filter.estateId = null;
      }

      if (values.status) filter.status = String(values.status);

      const start = formatDateValue(values.start);
      const end = formatDateValue(values.end);
      if (start) filter.start = start;
      if (end) filter.end = end;

      if (values.accountManager) {
        filter.accountManager = String(values.accountManager);
      }

      return appendEstateUnitIdToFilter(
        filter as Record<string, unknown>,
        String(values.estateUnitId || '')
      ) as ApiFilters;
    },
    [defaultEstateId, isEstateUser, singleEstateId]
  );

  const filterFields: FilterField[] = useMemo(() => {
    const fields: FilterField[] = [
      {
        kind: 'search',
        name: 'search',
        label: 'Pesquisar',
        placeholder: 'Digite...',
        defaultType: 'name',
        types: typeOptions.map(option => ({
          value: String(option.value),
          label: String(option.text)
        })),
        width: 1.6
      },
      {
        kind: 'select',
        name: 'insurerId',
        label: 'Seguradora',
        placeholder: 'Selecionar...',
        options: toSelectOptions(insurerOptions),
        getDisplayValue: values => {
          const option = insurerOptions.find(
            item => String(item.value) === String(values.insurerId)
          );
          return option ? option.text : undefined;
        }
      }
    ];

    if (!isEstateUser) {
      fields.push({
        kind: 'select',
        name: 'estateId',
        label: 'Imobiliária',
        placeholder: 'Selecionar...',
        options: toSelectOptions(estateOptions),
        getDisplayValue: values => {
          const option = estateOptions.find(
            item => String(item.value) === String(values.estateId)
          );
          return option ? option.text : undefined;
        }
      });
    }

    fields.push(
      {
        kind: 'select',
        name: 'status',
        label: 'Status',
        placeholder: 'Selecionar...',
        options: toSelectOptions(statusSelectOptions),
        getDisplayValue: values => {
          const option = statusSelectOptions.find(
            item => String(item.value) === String(values.status)
          );
          return option ? option.text : undefined;
        }
      },
      {
        kind: 'dateRange',
        name: 'period',
        label: 'Período',
        startName: 'start',
        endName: 'end'
      },
      {
        kind: 'custom',
        name: 'estateUnitId',
        label: 'Unidade',
        advanced: true,
        getDisplayValue: values => {
          if (!values.estateUnitId) return undefined;
          if (values.estateUnitId === 'null') return 'Sem unidade';
          return String(values.estateUnitId);
        },
        render: ({ values, setValue }) => (
          <EstateUnitFilter
            estateId={String(
              values.estateId || activeEstateId || singleEstateId || ''
            )}
            value={values.estateUnitId || ''}
            onChange={value => setValue('estateUnitId', value)}
            upward
          />
        )
      }
    );

    if (!isEstateUser) {
      fields.push({
        kind: 'select',
        name: 'accountManager',
        label: 'Gestor de Contas',
        placeholder: 'Selecionar...',
        advanced: true,
        loading: isAccountManagerLoading,
        options: toSelectOptions(accountManagerOptions),
        getDisplayValue: values => {
          const option = accountManagerOptions.find(
            item => String(item.value) === String(values.accountManager)
          );
          return option ? option.text : undefined;
        }
      });
    }

    return fields;
  }, [
    isEstateUser,
    insurerOptions,
    estateOptions,
    statusSelectOptions,
    accountManagerOptions,
    isAccountManagerLoading,
    activeEstateId,
    singleEstateId
  ]);

  const isLenderInsuranceEnabledForItem = (item: ListCapitalizationInterface) => {
    const camelCaseFlag = item.estate?.lenderInsuranceEnabled;
    const snakeCaseFlag = (item.estate as any)?.lender_insurance_enabled;

    if (typeof camelCaseFlag === 'boolean') return camelCaseFlag;
    if (typeof snakeCaseFlag === 'boolean') return snakeCaseFlag;

    return globalLenderInsuranceEnabled;
  };

  const clearDeepLink = useCallback(() => {
    setCapitalizationId(null);

    const currentLocState = { ...(location.state as LocationState) };
    delete currentLocState.capitalizationId;

    const params = new URLSearchParams(location.search);
    params.delete('id');
    const search = params.toString();

    history.replace({
      ...location,
      search: search ? `?${search}` : '',
      state: currentLocState
    });
  }, [history, location]);

  const fetchCapitalizations = async () => {
    const userId = get('uid');
    const filters: any = {
      ...appliedFiltersRef.current,
      page: currentPage,
      onlyUnread: false,
      userId,
      isRv: true
    };

    const deepLinkId = capitalizationIdRef.current;
    if (deepLinkId) {
      filters.id = deepLinkId;
    }

    const response = await getCapitalizations(filters);

    if (currentPage === 1) setTotal(response.count);

    if (deepLinkId) {
      clearDeepLink();
    }

    return {
      data: response.data,
      count: currentPage === 1 ? response.count : total
    };
  };

  const {
    data: capitalizationsData,
    isFetching: isLoading,
    refetch: refetchCapitalizations
  } = useQuery(
    ['getCapitalizations', currentPage, appliedFilters],
    fetchCapitalizations,
    {
      keepPreviousData: true,
      refetchOnMount: true,
      staleTime: 5000,
      enabled: !!capitalizationId || (!!estates.length && isEnabledToFetch)
    }
  );

  useEffect(() => {
    setOpenFollowUp(!!idFollowUp.length);
  }, [idFollowUp]);

  useEffect(() => {
    if (!isBackOffice) {
      setIsEnabledToFetch(true);
    }
  }, [isBackOffice]);

  useEffect(() => {
    if (isEstateUser && defaultEstateId) {
      setAppliedFilters(prev => ({
        ...prev,
        estateId: prev.estateId || defaultEstateId
      }));
    }
  }, [defaultEstateId, isEstateUser]);

  const handleFilterSearch = (values: FilterValues) => {
    const next = normalizeToApiFilter(values);
    if (capitalizationIdRef.current) {
      clearDeepLink();
    }
    setAppliedFilters(next);
    setCurrentPage(1);
    setIsEnabledToFetch(true);
  };

  const handleFilterClear = () => {
    clearDeepLink();
    setCurrentPage(1);
    setIsEnabledToFetch(true);
  };

  const { data = [], count = 0 } = capitalizationsData || {};

  const openDetailModal = (capitalization: any) => {
    setSelectedCapitalization(capitalization);
    setOpenDetail(true);
  };

  async function callback(fileTypes: string[], capitalization_id: string) {
    if (fileTypes) {
      let message = 'Foram anexados os seguintes arquivos: ';
      message += `${fileTypes.join(', ')}.`;
      try {
        await sendMessage(
          capitalization_id,
          user.id,
          message,
          user.name,
          'CAPITALIZATION',
          null,
          null,
          capitalization_id,
          false
        );
      } catch (err) {
        console.log(err);
      }
    }
  }

  const handleCalcUnread = (followup: FollowUpType[], idUser: string) => {
    if (idUser === '') return 0;

    if (!followup || followup.length === 0) return 0;

    let unreadCount = 0;
    followup.forEach(item => {
      const dontCount = user.role.includes('ESTATE') && item.isInternal;
      if (item.viewedBy && item.viewedBy?.length > 0 && !dontCount) {
        if (!item.viewedBy.includes(idUser)) unreadCount++;
      }
    });
    return unreadCount;
  };

  return (
    <>
      {openFollowUp && (
        <FollowUpModal
          isOpen={openFollowUp}
          onOpen={handleOpen}
          onClose={handleClose}
          header="Follow Up"
          id={bondInsuranceIdOpen}
          context={'CAPITALIZATION'}
          user={user}
        />
      )}

      <div style={{ marginBottom: 16 }}>
        <FilterBar
          fields={filterFields}
          initialValues={initialFilterValues}
          onSearch={handleFilterSearch}
          onClear={handleFilterClear}
          loading={isLoading}
          syncToUrl
          urlNamespace="capitalizations"
          hasExternalActiveFilters={Boolean(capitalizationId)}
        />
      </div>

      <Table style={{ margin: '0 25px 20px' }} celled>
        <Table.Header fullWidth>
          <Table.Row>
            <Table.HeaderCell textAlign={'center'}>Solicitado em</Table.HeaderCell>
            <Table.HeaderCell textAlign={'center'}>Seguradora</Table.HeaderCell>
            <Table.HeaderCell textAlign={'center'}>Imobiliária</Table.HeaderCell>
            <Table.HeaderCell textAlign={'center'}>Inquilino</Table.HeaderCell>
            <Table.HeaderCell textAlign={'center'}>Endereço</Table.HeaderCell>
            <Table.HeaderCell textAlign={'center'}>Status</Table.HeaderCell>
            <Table.HeaderCell textAlign={'center'}>Ações</Table.HeaderCell>
          </Table.Row>
        </Table.Header>
        <Dimmer.Dimmable as={Table.Body} dimmed={isLoading}>
          {isLoading && (
            <Dimmer active blurring>
              <Loader />
            </Dimmer>
          )}
          {data && data.length > 0 ? (
            <>
              {data.map((item: ListCapitalizationInterface) => {
                const unread = handleCalcUnread(item.followup ?? [], user.id ?? '');
                const {
                  id,
                  createdAt,
                  insurer,
                  estate,
                  status,
                  capitalizationTenant,
                  capitalizationProperty
                } = item;

                const fullAddress = `${capitalizationProperty.street}, ${
                  capitalizationProperty.number
                }${
                  capitalizationProperty.complement
                    ? ` ${capitalizationProperty.complement}`
                    : ''
                } - ${capitalizationProperty.district} - ${capitalizationProperty.city} - ${
                  capitalizationProperty.state
                }`;
                const address = `${capitalizationProperty.street}, ${
                  capitalizationProperty.number
                }${
                  capitalizationProperty.complement
                    ? ` ${capitalizationProperty.complement}`
                    : ''
                }`;
                const popupAddress = () => (
                  <>
                    <span>{fullAddress}</span>
                  </>
                );
                const createdAtFormatted = moment(createdAt)
                  .format(`DD/MM/YYYY HH:mm`)
                  .split(' ');
                return (
                  <>
                    <ClickableTableRow key={id!}>
                      <ClickableTableCell
                        onClick={() => openDetailModal(item)}
                        textAlign={'center'}
                        collapsing
                      >
                        {createdAtFormatted[0]} <br /> {createdAtFormatted[1]}
                      </ClickableTableCell>
                      <ClickableTableCell
                        onClick={() => openDetailModal(item)}
                        textAlign={'center'}
                        width={2}
                        style={{
                          textOverflow: 'ellipsis',
                          wordWrap: 'nowrap'
                        }}
                      >
                        {insurer?.name ?? (
                          <span style={{ color: '#ccc' }}>Não atribuído</span>
                        )}
                      </ClickableTableCell>
                      <ClickableTableCell
                        onClick={() => openDetailModal(item)}
                        textAlign={'center'}
                      >
                        {estate?.name}
                      </ClickableTableCell>
                      <ClickableTableCell
                        onClick={() => openDetailModal(item)}
                        textAlign={'center'}
                      >
                        {capitalizationTenant.name}
                        <br />
                        <span style={{ color: '#777' }}>
                          ({maskCpfOrCnpj(capitalizationTenant.document)})
                        </span>
                      </ClickableTableCell>
                      <ClickableTableCell
                        onClick={() => openDetailModal(item)}
                        style={{ cursor: 'default' }}
                        textAlign={'center'}
                      >
                        <Popup
                          content={popupAddress}
                          as={Segment}
                          style={{ border: '2px solid #dfdfdf' }}
                          position={'left center'}
                          trigger={
                            <>
                              <span style={{ cursor: 'help' }}>{address}</span>
                              <br />
                              <span>
                                {item.locationType === PurposesEnum.residential
                                  ? '(Residêncial)'
                                  : '(Comercial)'}
                              </span>
                            </>
                          }
                        />
                      </ClickableTableCell>
                      <ClickableTableCell
                        onClick={() => openDetailModal(item)}
                        textAlign={'center'}
                      >
                        {translateStatusCapitalization(status)}
                      </ClickableTableCell>

                      <Table.Cell width={1}>
                        <Menu compact>
                          <Menu.Item>
                            <Button
                              primary
                              icon="chat"
                              onClick={() => {
                                setIdFollowUp(id);
                                setBondInsuranceIdOpen(id);
                                setOpenFollowUp(true);
                              }}
                            />
                            {unread !== 0 && (
                              <Label color="red" circular floating>
                                {unread}
                              </Label>
                            )}
                          </Menu.Item>
                          {!isEstateQuotationUser && (
                            <Menu.Item>
                              <ModalAttachFiles
                                message={`Arquivos anexos para capitalização`}
                                trigger={<Button color="teal" icon="attach" />}
                                bondInsuranceId={id}
                                tableName={'capitalization'}
                                fileIdentifiers={FileIdentifiersCapitalizationType}
                                callback={data => callback(data, id)}
                                brokerOnly={true}
                              />
                            </Menu.Item>
                          )}
                          {!isEstateQuotationUser &&
                            isLenderInsuranceEnabledForItem(item) &&
                            (() => {
                              const tenantDocument = (
                                item.capitalizationTenant?.document || ''
                              ).replace(/\D/g, '');
                              const isTenantPf = tenantDocument.length === 11;
                              if (!isTenantPf) return null;

                              const tenantBirthDate =
                                item.capitalizationTenant?.birthDate || '';
                              const tenantAge = tenantBirthDate
                                ? calculateAge(tenantBirthDate)
                                : 0;
                              if (tenantAge >= 65) return null;

                              return (
                                <Menu.Item>
                                  <Popup
                                    content={
                                      item.lenderInsuranceId
                                        ? 'Gerenciar Seguro Prestamista'
                                        : 'Adicionar Seguro Prestamista'
                                    }
                                    trigger={
                                      <Button
                                        color={
                                          item.lenderInsuranceId ? 'green' : 'purple'
                                        }
                                        icon="shield"
                                        basic={!item.lenderInsuranceId}
                                        onClick={() => {
                                          setSelectedCapitalizationForLender(item);
                                          setOpenLenderInsuranceModal(true);
                                        }}
                                      />
                                    }
                                  />
                                </Menu.Item>
                              );
                            })()}
                          {!isEstateQuotationUser && (
                            <AuthorizationContainer
                              action={ActionEnum.update}
                              feature={FeatureEnum.captalizations}
                            >
                              <Menu.Item>
                                <EditCapitalizationModal
                                  capitalization={item}
                                  trigger={
                                    <Button color="yellow" icon="pencil alternate" />
                                  }
                                  insurers={insurers}
                                />
                              </Menu.Item>
                            </AuthorizationContainer>
                          )}
                        </Menu>
                      </Table.Cell>
                    </ClickableTableRow>
                  </>
                );
              })}
            </>
          ) : (
            <Table.Row>
              <Table.Cell colSpan={9} textAlign={'center'}>
                <span>
                  {getListEmptyMessage(props.user?.role, props.user?.estateUnitId)}
                </span>
              </Table.Cell>
            </Table.Row>
          )}
        </Dimmer.Dimmable>
        <Table.Footer fullWidth>
          <Table.Row>
            <Table.HeaderCell colSpan={9} textAlign={'right'}>
              {!isLoading && (
                <TablePaginationMenu
                  currentPage={currentPage}
                  dataCount={count}
                  setCurrentPage={setCurrentPage}
                />
              )}
            </Table.HeaderCell>
          </Table.Row>
        </Table.Footer>
      </Table>
      <DetailModal
        open={openDetail}
        capitalization={selectedCapitalization}
        close={() => setOpenDetail(false)}
      />
      {selectedCapitalizationForLender && (
        <CapitalizationLenderInsuranceModal
          open={openLenderInsuranceModal}
          onClose={() => {
            setOpenLenderInsuranceModal(false);
            setSelectedCapitalizationForLender(null);
          }}
          onSuccess={() => {
            refetchCapitalizations();
          }}
          capitalization={selectedCapitalizationForLender}
          rate={
            selectedCapitalizationForLender?.estate?.lenderInsuranceRate ??
            globalLenderInsuranceRate ??
            19.9
          }
          isTenantPF={
            (
              selectedCapitalizationForLender?.capitalizationTenant?.document || ''
            ).replace(/\D/g, '').length === 11
          }
        />
      )}
      <PopupModal
        locationActivation={BannerLocationActivationEnum.CAPIITALIZATION_LIST}
      />
    </>
  );
};
export default connect(mapState, mapDispatch)(CapitalizationTable);
